import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { ShieldCheck, ChevronLeft, Plus, Minus, Star, Loader2, Plane, Ship, Truck, Package, Check } from "lucide-react";

export default function PartDetail() {
  const { id } = useParams();
  const [part, setPart] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/parts/${id}`)
      .then(r => setPart(r.data))
      .catch(() => toast.error("Part not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#009A44]" size={28} /></div>;
  if (!part) return <div className="max-w-[1400px] mx-auto px-4 py-20 text-center">Part not found.</div>;

  const addToCart = () => {
    add(part, qty);
    toast.success(`${qty} × ${part.name.slice(0, 40)}… added to cart`);
  };

  const buyNow = () => {
    add(part, qty);
    nav("/cart");
  };

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="part-detail-page">
      <Link to="/search" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-[#121212] mb-6">
        <ChevronLeft size={16} /> Back to results
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square bg-zinc-100 overflow-hidden border border-[#E4E4E7]">
            <img src={part.image_url} alt={part.name} className="w-full h-full object-cover" />
          </div>
          <div className="mt-3 flex gap-2">
            {part.verified_by_agent && (
              <div className="bg-[#FFD100]/95 text-[#121212] border border-[#121212] text-xs font-bold uppercase tracking-wider px-3 py-2 flex items-center gap-1" data-testid="verified-badge">
                <ShieldCheck size={14} /> Agent Verified
              </div>
            )}
            <div className="bg-[#121212] text-white text-xs font-semibold uppercase px-3 py-2">
              {part.condition}
            </div>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
            {part.category} · {part.brand}
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-4" data-testid="part-name">
            {part.name}
          </h1>
          <p className="text-zinc-700 mb-4 leading-relaxed">{part.description}</p>
          {part.rating > 0 && (
            <div className="flex items-center gap-2 mb-6 text-sm">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={14} fill={i <= Math.round(part.rating) ? "#FFD100" : "transparent"} stroke="#FFD100" />
                ))}
              </div>
              <span className="font-semibold">{part.rating}</span>
              <span className="text-zinc-500">({part.reviews_count} reviews)</span>
            </div>
          )}

          <div className="border-y border-[#E4E4E7] py-6 mb-6">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Price</div>
            <div className="font-display text-5xl font-extrabold mb-1" data-testid="part-price-large">
              {formatMoney(part.price, part.currency)}
            </div>
            <div className="text-sm text-[#009A44] font-semibold">In stock · {part.stock}+ available globally</div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center border-2 border-[#121212]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-zinc-100" data-testid="qty-minus">
                <Minus size={16} />
              </button>
              <div className="px-6 py-3 font-bold min-w-[60px] text-center" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-zinc-100" data-testid="qty-plus">
                <Plus size={16} />
              </button>
            </div>
            <button onClick={addToCart} className="btn-outline-dark flex-1" data-testid="add-to-cart-button">
              Add to cart
            </button>
            <button onClick={buyNow} className="btn-primary flex-1" data-testid="buy-now-button">
              Buy now
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-6">
            <TrustRow icon={<Check size={14} />} label="48hr availability lock" />
            <TrustRow icon={<ShieldCheck size={14} />} label="Agent inspected" show={part.verified_by_agent} />
            <TrustRow icon={<Truck size={14} />} label="Global sourcing" />
            <TrustRow icon={<Package size={14} />} label="Secure escrow payment" />
          </div>

          <div className="bg-zinc-50 border border-[#E4E4E7] p-5">
            <div className="font-display font-bold uppercase tracking-widest text-xs mb-3">Compatibility</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-zinc-500 text-xs uppercase">Makes</div>
                <div className="font-semibold">{part.compatibility.makes.join(", ")}</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase">Models</div>
                <div className="font-semibold">{part.compatibility.models.join(", ")}</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase">Years</div>
                <div className="font-semibold">
                  {Math.min(...part.compatibility.years)}–{Math.max(...part.compatibility.years)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 text-xs">
            <div className="border border-[#E4E4E7] p-3 flex items-center gap-2">
              <Plane size={16} className="text-[#009A44]" /> <span><b>Air:</b> 3–7 days</span>
            </div>
            <div className="border border-[#E4E4E7] p-3 flex items-center gap-2">
              <Ship size={16} className="text-[#121212]" /> <span><b>Sea:</b> 21–45 days</span>
            </div>
          </div>

          <div className="mt-5 text-xs text-zinc-500">
            <b className="text-zinc-700">Part number:</b> <span className="font-mono">{part.part_number}</span> ·
            <b className="text-zinc-700 ml-2">Ships from:</b> {part.seller_name} ·
            <b className="text-zinc-700 ml-2">Weight:</b> {part.weight_kg} kg
          </div>
        </div>
      </div>
    </main>
  );
}

function TrustRow({ icon, label, show = true }) {
  if (!show) return null;
  return (
    <div className="flex items-center gap-2 text-xs bg-[#009A44]/10 text-[#007A33] border border-[#009A44]/30 px-3 py-2">
      {icon} <span className="font-semibold">{label}</span>
    </div>
  );
}
